import Backbone from 'backbone'
import _ from 'lodash'
import nestModel from 'scoreboard/components/nest-model'
import DashboardData from '../dashboard-data'
import MockSync from 'scoreboard/components/mock-sync'
import unwrapModel from 'scoreboard/components/unwrap-model'

const DEFAULT_BACKGROUND = 'white'

var WidgetModel = Backbone.Model.extend({
	// use a mocked sync so that changes aren't sent to the server immediately
	sync: MockSync,

	initialize: function (attr, options = {}) {
		this.options = options

		// For function-based minHeight and minWidth to be useful, we need to bind the
		// functions to the Model (instead of containerDefaults). But since containerDefaults
		// is defined on the prototype, we'll want to clone it before rebinding, or
		// widgets of the same type will be stepping on each other
		this.containerDefaults = _.cloneDeep(this.containerDefaults || {})
		_(['minHeight', 'minWidth'])
			.filter(d => typeof this.containerDefaults[d] === 'function')
			.forEach(d => this.containerDefaults[d] = this.containerDefaults[d].bind(this))

		// #29977 default widgets to a solid background
		const props = this.get('properties')
		_.defaults(props, { background: DEFAULT_BACKGROUND })


		this.properties = new Backbone.Model(props || {})
		nestModel({
			parent: this,
			parentAttribute: 'properties',
			child: this.properties
		})

		var data = attr?.data
		// We're keeping the domain object data on this.data, so we don't need it
		// in the Backbone.Model attributes. Clearing it out of there makes serializing
		// the model (ie toJSON()) faster, which is important for undo/redo processing
		// in large dashboards (see #19664)
		delete this.attributes.data

		if (!data && options.domainObject) {
			// If we have no data, but a domainObject was passed in, this is probably a new node.
			// Create some very basic data using what we know (id, label, etc)
			data = {}
			var section = this.section()
			if (section) {
				data[section] = {}
				data[section][options.domainObject.id] =
					unwrapModel(options.domainObject)
			}
		}

		this.data = new DashboardData({
			data: data,
			parentData: options.parentData,
			fetchOptions: this._fetchOptions.bind(this)
		})

		if (!this.get('type')) {
			this.set('type', this.properties.get('type'))
		}

		// Pull any initial UI state out of the attributes, since they shouldn't
		// be persisted
		this.initialState = attr?.state
		delete this.attributes.state
	},

	_fetchOptions: function (opt) {
		opt = opt || {}

		var url = 'api/dashboards/data?calendarPeriodId=' + opt.periodId
		if (this.options.dashboardId != null) {
			url += '&dashboardId=' + this.options.dashboardId
		}

		let data = {
			widgets: [ _.extend({
				properties: this.properties.toJSON()
			}, opt.data) ],
			liveFilters: this._maestro && this._maestro.liveFilters()
		}

		// Send all dashboard node properties to the server in case they have changed
		return {
			type: 'POST',
			url: url,
			data: JSON.stringify(data)
		}
	},

	/**
	 * Returns or sets the maestro instance for this widget
	 */
	maestro: function (maestro) {
		if (arguments.length === 0) {
			return this._maestro
		} else {
			this._maestro = maestro
		}
	},

	/*
	 * Given a hash of property names and values, returns the widget property
	 * updates that can be updated via multiple selection.
	 *
	 * For example, given considerProperties({ showScore: true, showPrevious: true })
	 * a performance bubble would return { showScore: true }
	 * and a speedometer would return { showScore: true, showPrevious: true }
	 *
	 * The default implementation reads this.multiSettableProps (can be an array or
	 * a function returning an array) to determine which properties can be applied.
	 * However, this function can be overridden if necessary.
	 */
	considerProperties: function (props) {
		if (this.wob.get('locked')) {
			// if the wob is locked, don't apply any multi-selection changes
			return
		}

		var update = _(props).toPairs()
			.filter(function (pair) {
				return _.includes(_.result(this, 'multiSettableProps'), pair[0])
			}.bind(this))
			.fromPairs().value()

		return update
	},

	outlineColor: function (color) {
		if (!this.canHaveOutline) {
			return
		}

		var update = { outlineColor: color }
		if (color && !this.wob.get('outlineWidth')) {
			update.outlineWidth = WidgetModel.DEFAULT_OUTLINE_WIDTH
		}

		this.wob.set(update)
	},

	outlineWidth: function (width) {
		if (!this.canHaveOutline) {
			return
		}

		var update = { outlineWidth: width }
		if (width && !this.wob.get('outlineColor')) {
			update.outlineColor = WidgetModel.DEFAULT_OUTLINE_COLOR
		}

		this.wob.set(update)
	},

	isInteractive: function () {
		// By default, all widgets are interactive, meaning they respond to
		// users click events. Used by the canvas to control zoomability. #20751
		return true
	},

	isClickthrough() {
		// By default, click events on widgets should be handled by that widget,
		// instead of letting them pass through to wobs underneath
		return false
	},

	/**
	 * Sets the background for the widget. May be overridden (ex. performance-bubble-model)
	 */
	setBackground(background) {
		this.properties.set({ background })
	},

	/**
	 * If called with no arguments, returns the drilldown section and ID (or null if no drilldown). Example:
	 *   { section: 'scorecards', id: 1701 }
	 *
	 * If called with an argument, sets the drilldown section and ID. Examples:
	 *   { section: 'scorecards', id: 1701 }
	 *   { section: 'default' }
	 *   { section: 'none' }
	 */
	drilldown: function (drilldown) {
		if (drilldown) {
			var update = {
				drilldownSection: drilldown.section,
				drilldownId: drilldown.id,
				organizationId: drilldown.organizationId,
				projectId: drilldown.projectId
			}

			if (this.properties.get('type') === 'initiative-timeline') {
				update.drilldownOrganizationId = update.organizationId
				delete update.organizationId
			}

			if (this.wob.get('hasTemplate')) {
				// #11103 if we're overriding the drilldown in a template copy,
				// set the enableExcludedProperties to indicate it should be
				// left alone in the future when propagating changes from
				// the template
				update.enableExcludedProperties = true
			}

			this.properties.set(update)

			return
		}

		var props = this.properties.toJSON()
		var section = props.drilldownSection || 'none'

		if (section === 'none') {
			return null
		}

		if (section == 'default' && _.isNil(this.section())) {
			// If there is no data source, then there is no "default" to
			// drilldown to
			return null
		}

		section = section == 'default' ? this.section() : section
		let id = props.drilldownSection === 'default'
			? props.domainObjectId : props.drilldownId

		// Get the organizationId for the domain object in this widget using
		// synthesized functions on dashboard-data.js
		const orgIdPropName = props.type === 'initiative-timeline' ?
			'drilldownOrganizationId' : 'organizationId'
		const orgId = _.get(this.data[section](id), orgIdPropName)

		if (orgId) {
			let organizations = this.data.organizations()
			let projectId

			if (organizations) {
				projectId = _.get(organizations[orgId], 'projectId')
			}
			return { section, id, orgId, projectId }
		} else {
			return { section, id }
		}
	},

	/**
	 * Returns text that should show when hovering over the widget (ie "alt text").
	 *
	 * @param domainObjectId ID of the domain object (scorecard, dashboard, etc) that
	 * the widget is displaying
	 *
	 * @param displayedText the relevant text that is visible to the user.
	 * Examples: the text in the bubble for performance bubbles, or the
	 * scorecard item label for speedometers
	 */
	hoverText: function ({ domainObjectId, displayedText }) {
		let section = _.result(this, 'section')
		if (!section || section === 'none') {
			return null
		}

		let domainObject = this.data[section](domainObjectId)
		if (!domainObject) {
			return null
		}

		let props = this.properties.toJSON()
		let drilldown = this.drilldown()

		// See https://github.com/SpiderStrategies/Scoreboard/issues/22891#issuecomment-470279344
		// for rules on how hover text is chosen
		let hoverText
		if (drilldown && props.drilldownSection !== 'default') {
			let drilldownObject = this.data[drilldown.section](drilldown.id)
			if (drilldownObject) {
				hoverText = drilldownObject.label
			}
		} else if (domainObject && domainObject.description) {
			hoverText = domainObject.description
		} else if (displayedText) {
			hoverText = displayedText
		}

		if (!hoverText && props.drilldownSection === 'default') {
			hoverText = domainObject.label
		}

		return hoverText
	},

	/**
	 * Sets this.properties without firing `change` events. Analogous to:
	 *
	 *   Backbone.Model.set(key, value, { silent: true })
	 *
	 * But safer to use for WidgetModel.properties, since nest-model needs the
	 * change events to keep model.properties and model.attributes.properties
	 * in sync.
	 */
	setPropertySilently (key, value) {
		this.properties.attributes[key] = value
		this.attributes.properties[key] = value
	},

	/**
	 * Returns the value of a widget property as a number, or null if the
	 * property is not set or is an empty string.
	 *
	 * @param {String} key
	 *
	 * @return {number|null}
	 *
	 * @throws {Error} if the property is a non-empty string that is not a number
	 */
	getNumericProperty (key) {
		let value = this.properties.get(key)
		if (_.isNumber(value)) {
			return value
		} else if (_.isString(value) && value.trim().length > 0) {
			let num = Number(value.trim())
			if (isNaN(num)) {
				throw new Error(`Property "${key}" is not a number: "${value}"`)
			} else {
				return num
			}
		} else {
			return null
		}
	}

})

WidgetModel.DEFAULT_OUTLINE_COLOR = 'gray-darker'
WidgetModel.DEFAULT_OUTLINE_WIDTH = '1px'

export default WidgetModel

WidgetModel.__filename__ = 'dashboards/widgets/widget-model.js'
